import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Pagination, Response } from '../../../_shared/models';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  GetFuelReconKpiSummaryRequest,
  GetFuelReconKpiSummaryResponse,
} from '../models/fuel-recon-kpi.model';
import { environment } from '../../../../environments/environment';
import {
  PaginatedFuelToReconcileRequest,
  FuelToReconcile,
  ReconciledFuel,
  PaginatedReconciledFuelRequest,
  FuelSlipUploadPreference,
  UpsertFuelSlipUploadPreferenceRequest,
  ReconcileFuelSlipRequest,
  UpdateReconciledFuelSlipRequest,
  BulkReconcileFuelSlipRequest,
  CurrencyOption,
} from '../models/fuel-recon.model';

@Injectable({
  providedIn: 'root',
})
export class FuelReconciliationService {
  private static readonly fuelSlipPreferenceCacheKey = 'v2.fuelSlipUploadPreference';

  constructor(private http: HttpClient) { }

  public async getKpiSummary(
    request: GetFuelReconKpiSummaryRequest,
    cancellation: Subject<void>
  ): Promise<Response<GetFuelReconKpiSummaryResponse>> {
    return firstValueFrom(
      this.http
        .post(`${environment.apiEndpoint}/fuelrecon/kpi-cards-summary`, request)
        .pipe(takeUntil(cancellation))
    );
  }

  public getPaginatedFuelToReconcileByAccountIdAsync(
    request: PaginatedFuelToReconcileRequest,
    cancellation: Subject<void>
  ): Promise<Response<Pagination<FuelToReconcile[]>>> {
    return firstValueFrom(
      this.http
        .post(`${environment.apiEndpoint}/fuelrecon/fuel-to-reconcile`, request)
        .pipe(takeUntil(cancellation))
    );
  }

  public getPaginatedReconciledFuelByAccountIdAsync(request: PaginatedReconciledFuelRequest, cancellation: Subject<void>): Promise<Response<Pagination<ReconciledFuel[]>>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelrecon/reconciled-fuel`, request).pipe(takeUntil(cancellation)));
  }

  public async getFuelSlipUploadPreferenceAsync(cancellation: Subject<void>): Promise<Response<FuelSlipUploadPreference>> {
    const cached = localStorage.getItem(FuelReconciliationService.fuelSlipPreferenceCacheKey);
    if (cached) {
      return Promise.resolve({
        data: JSON.parse(cached),
        message: 'Loaded from cache',
        success: true,
      } as Response<FuelSlipUploadPreference>);
    }

    const response = await firstValueFrom(
      this.http
        .get<Response<FuelSlipUploadPreference>>(`${environment.apiEndpoint}/fuelrecon/fuel-slip-user-upload-preference`)
        .pipe(takeUntil(cancellation))
    );

    if (response?.data) {
      localStorage.setItem(FuelReconciliationService.fuelSlipPreferenceCacheKey, JSON.stringify(response.data));
    }

    return response
  }

  public async upsertFuelSlipUploadPreferenceAsync(preference: UpsertFuelSlipUploadPreferenceRequest, cancellation: Subject<void>): Promise<Response<void>> {
    const response = await firstValueFrom(
      this.http
        .post<Response<void>>(`${environment.apiEndpoint}/fuelrecon/upsert-fuel-slip-user-upload-preference`, preference)
        .pipe(takeUntil(cancellation))
    );

    if (!response?.errorCode) {
      localStorage.setItem(FuelReconciliationService.fuelSlipPreferenceCacheKey, JSON.stringify(preference));
    }

    return response;
  }

  public async reconcileFuelSlipAsync(request: ReconcileFuelSlipRequest, file: File | null, cancellation: Subject<void>): Promise<Response<void>> {
    const formData = new FormData();
    formData.append('fuelSlipId', request.fuelSlipId.toString());
    formData.append('assetName', request.assetName);
    if (request.assetId != null) formData.append('assetId', request.assetId.toString());
    formData.append('amount', request.amount.toString());
    formData.append('cost', request.cost.toString());
    formData.append('isTotalCost', (request.isTotalCost ?? false).toString());
    formData.append('createdBy', request.createdBy);
    formData.append('currencyId', request.currencyId.toString());
    if (file) {
      formData.append('image', file, file.name);
    }
    const response = await firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelrecon/reconcile-fuel-slip`, formData).pipe(takeUntil(cancellation)));
    return response;
  }

  public async updateReconciledFuelSlipAsync(request: UpdateReconciledFuelSlipRequest, file: File | null, cancellation: Subject<void>): Promise<Response<void>> {
    const formData = new FormData();
    formData.append('fuelSlipImportsId', request.fuelSlipImportsId.toString());
    formData.append('amount', request.amount.toString());
    formData.append('cost', request.cost.toString());
    formData.append('isTotalCost', request.isTotalCost.toString());
    formData.append('createdBy', request.createdBy);
    formData.append('currencyId', request.currencyId.toString());
    if (file) {
      formData.append('image', file, file.name);
    }
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelrecon/update-reconciled-fuel-slip`, formData).pipe(takeUntil(cancellation)));
  }

  public async deleteReconciledFuelSlipAsync(fuelSlipImportsId: number, cancellation: Subject<void>): Promise<Response<void>> {
    return firstValueFrom(
      this.http
        .post<Response<void>>(`${environment.apiEndpoint}/fuelrecon/delete-reconciled-fuel-slip/${fuelSlipImportsId}`, {})
        .pipe(takeUntil(cancellation))
    );
  }

  public async deleteFuelSlipAttachmentAsync(fuelSlipImportsId: number, cancellation: Subject<void>): Promise<Response<void>> {
    return firstValueFrom(
      this.http
        .post<Response<void>>(`${environment.apiEndpoint}/fuelrecon/delete-fuel-slip-attachment/${fuelSlipImportsId}`, {})
        .pipe(takeUntil(cancellation))
    );
  }

  public async getFuelSlipAttachmentUrlAsync(fuelSlipImportsId: number, cancellation: Subject<void>): Promise<Response<string>> {
    return firstValueFrom(
      this.http
        .get<Response<string>>(`${environment.apiEndpoint}/fuelrecon/fuel-slip-attachment/${fuelSlipImportsId}`)
        .pipe(takeUntil(cancellation))
    );
  }

  public async bulkReconcileFuelSlipsAsync(bulkReconcileRequest: BulkReconcileFuelSlipRequest, cancellation: Subject<void>): Promise<Response<void>> {
    return firstValueFrom(
      this.http
        .post<Response<void>>(`${environment.apiEndpoint}/fuelrecon/bulk-reconcile-fuel-slips`, bulkReconcileRequest)
        .pipe(takeUntil(cancellation))
    );
  }

  public async getCurrencyOptionsAsync(cancellation: Subject<void>): Promise<Response<CurrencyOption[]>> {
    return firstValueFrom(
      this.http
        .get<Response<CurrencyOption[]>>(`${environment.apiEndpoint}/currency/currency-options`)
        .pipe(takeUntil(cancellation))
    );
  }
}